const events = {
  affiliate_link: ["click"],
  analytics: ["user_cookies", "create_lead"],
  batch: ["backup_page", "command"],
  brand: ["attach_resource", "get_brands", "get_logo", "not_found", "create"],
  checkout_modal: ["international_shipping_offer_missing"],
  configuration: ["missing_secret"],
  content_search: ["did_you_mean_gpt"],
  credit: ["get_count", "redeemed"],
  display_offer: [],
  decisions: ["personalization"],
  domain: ["get_domains"],
  email: [
    "introductory_offer",
    "password_reset",
    "redeem_product",
    "update_profile",
    "welcome",
  ],
  email_config: ["upsert"],
  events: ["initialization", "file_resource", "cms_page"],
  featureFlags: ["get_feature_flags"],
  integrations: [
    "initialization",
    "serialization",
    "order_tracking",
    "lead_tracking",
    "action_tracking",
  ],
  internal: [
    "attributes",
    "attributes.values",
    "attributes.external_relationships",
    "jobs",
    "marketing.ads",
    "marketing.resources",
    "marketing.attributes",
    "marketing.entity_types",
    "marketing.entity_attribute_values",
    "marketing.entity_attributes",
    "marketing.pages",
    "marketing.ad_groups",
    "marketing.ad_group_ads",
    "marketing.emails",
    "marketing.resources/consumers",
    "slack",
    "entity.search",
    "resource_consumers.create",
    "resource_consumers.list",
    "resource_consumers.delete",
    "shipments",
    "shipments.values",
    "lx.auto_pauses",
  ],
  invoice: ["get_all"],
  lead: ["captured", "found"],
  linear: [],
  marketing_site: ["404"],
  marketing: ["ads"],
  attributes: [
    "attributes",
    "values",
    "entity_attributes",
    "entity_types",
    "entity_search",
  ],
  modal: ["get_redirect_url", "load"],
  offer: ["create", "get_available", "not_found"],
  order: [
    "create",
    "get_information",
    "mark_uncollectible",
    "mark_failed",
    "update",
  ],
  order_line_item: ["create", "update"],
  package: ["create"],
  page: ["update"],
  payment: [
    "adjust_invoices",
    "charge_invoice",
    "exchange",
    "failure",
    "first",
    "first_non_0",
    "missing_required_info",
    "missing_tracking_info",
    "one_time",
    "payoff",
    "refund",
    "refund_and_purchase",
    "requires_action",
    "status_logger",
    "subscription",
    "typeof_purchase_error",
    "update_statement_descriptor",
    "charge_payment_plan_invoice",
    "charge_payment_plan_retry",
    "unhandled_retry_status",
    "process_outstanding_invoices",
    "process_future_invoices",
    "process_retry_invoices",
    "manual_retry_invoice",
  ],
  poll: ["load", "respond", "create", "update"],
  product: ["delete"],
  resource: ["create_resource_consumer", "root", "[id]"],
  resource_consumer: ["root", "[id]", "[consumerId]"],
  raw_webhook: ["save", "check_duplicate_raw_webhook"],
  rip_n_ship: [
    "begin_processing",
    "error_processing",
    "page_backup",
    "file_download",
    "fastcdn_matches",
    "command_error",
    "tracking",
  ],
  router: [
    "case_config",
    "rule_config",
    "target_config",
    "router_config",
    "delete_previous_versions",
    "update_previous_versions",
    "update_all_versions",
    "updated_existing_case_target",
  ],
  s3: ["presigned_url"],
  shippo: [
    "verify_address",
    "process_webhook",
    "value_order",
    "process_order",
    "pick_best_rate",
    "create_customs",
    "create_shipment",
  ],
  shipments: ["load"],
  slack: [
    "create_message",
    "failed_subscription_payment",
    "new_sale",
    "requires_action",
    "unvalidated",
  ],
  status: [],
  stripe: [
    "attach_payment_method",
    "buy",
    "construct_webhook",
    "create_customer",
    "create_price",
    "create_recurring_price",
    "create_subscription",
    "customer_deleted",
    "found_customer",
    "get_payment_methods",
    "load",
    "missing_value_in_metadata",
    "unknown_data_type",
    "no_default_payment_method",
    "payment_provider",
    "process_webhook",
    "remove_payment_method",
    "setup_intent",
    "update_default_payment_method",
    "parsing_webhook",
  ],
  subscription: ["created", "payment_succeeded"],
  test: ["user_delete"],
  testimonial: ["upsert"],
  tag: ["adding_tags_to_text"],
  timer: ["load", "create", "update", "delete"],
  user: [
    "associate_with_product",
    "create_action_log",
    "create_shipping_address",
    "find_or_create",
    "found",
    "get_orders",
    "get_user_products",
    "learning_progress",
    "login",
    "not_found",
    "notes",
    "revoke_access",
    "update_access",
    "update_password",
    "user_search",
    "cc_set_user_password",
  ],
  webinar: [
    "create_prerecorded_messages_for_instance",
    "send_message",
    "registration_started",
    "registration",
    "thank_you_page",
    "duplicate",
  ],
  zendesk: ["create_instance", "create_webinar_message_ticket", "get_tickets"],
} as const;

export type Category = keyof typeof events;
export type SubCategory<C extends Category> = (typeof events)[C][number];

type LogLevels = "info" | "warn" | "error" | "critical" | "debug";

type EventString<C extends Category> =
  | `${C}.${SubCategory<C>}.${LogLevels}`
  | `${C}.${LogLevels}`;

function createEventString<C extends Category>(
  category: C,
  result: LogLevels,
  subCategory?: SubCategory<C> | string
): EventString<C> | string {
  if (subCategory) {
    return `${category}.${subCategory}.${result}`;
  }
  return `${category}.${result}`;
}

const jsonSerializer = (_key: string, val: unknown) => {
  if (val instanceof Error) {
    return {
      name: val.name,
      message: val.message,
      stack: val.stack,
    };
  }

  return val;
};

function log({
  eventName,
  eventMessage,
  eventData,
  eventLevel,
}: {
  eventName: EventString<Category> | string;
  eventMessage: string;
  eventData: unknown;
  eventLevel: LogLevels;
}) {
  const env = process.env.ENV || "local";
  const logEvent = {
    event_name: "xms_api." + eventName,
    event_message: eventMessage,
    event_data: eventData,
    level: eventLevel.toUpperCase(),
    version: process.env.VERSION,
    service: "xms-api",
  };

  // eslint-disable-next-line no-console
  console.log(
    JSON.stringify(logEvent, jsonSerializer, env === "local" ? 4 : undefined)
  );
  // if (env == 'local') console.log("--------------")
}

export { createEventString, events, log };
